import axios from 'axios';
import { API_BASE_URL } from 'configs/AppConfig';
import { signOutSuccess } from 'store/slices/authSlice';
import store from '../store';
import { AUTH_TOKEN, AUTH_REFRESH_TOKEN } from 'constants/AuthConstant';
import { notification } from 'antd';

const unauthorizedCode = [401, 403]
const REFRESH_TOKEN_ENDPOINT = '/auth/refresh'

// Flag para evitar múltiplas chamadas de refresh
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  
  failedQueue = []
}

const service = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000
})

// Config
const TOKEN_PAYLOAD_KEY = 'Authorization'

// API Request interceptor
service.interceptors.request.use(config => {
	const jwtToken = localStorage.getItem(AUTH_TOKEN) || null;
	
	if (jwtToken) {
		config.headers[TOKEN_PAYLOAD_KEY] = `Bearer ${jwtToken}`
	}

  	return config
}, error => {
	notification.error({
		message: 'Erro na requisição'
	})
	return Promise.reject(error)
})

// API response interceptor
service.interceptors.response.use( (response) => {
	return response.data
}, async (error) => {
	const originalRequest = error.config

	// Se for erro 401 ou 403 e não for uma tentativa de refresh
	if (unauthorizedCode.includes(error.response?.status) && !originalRequest._retry) {
		if (isRefreshing) {
			// Se já está fazendo refresh, adiciona à fila
			return new Promise((resolve, reject) => {
				failedQueue.push({ resolve, reject })
			}).then(token => {
				originalRequest.headers[TOKEN_PAYLOAD_KEY] = `Bearer ${token}`
				return service(originalRequest)
			}).catch(err => {
				return Promise.reject(err)
			})
		}

		originalRequest._retry = true
		isRefreshing = true

		const refreshToken = localStorage.getItem(AUTH_REFRESH_TOKEN)
		
		if (!refreshToken) {
			// Não há refresh token, fazer logout
			processQueue(error, null)
			isRefreshing = false
			handleLogout()
			return Promise.reject(error)
		}

		try {
			// Extrair o ID do refresh token
			const refreshTokenId = extractRefreshTokenId(refreshToken)
			
			const response = await axios.post(`${API_BASE_URL}${REFRESH_TOKEN_ENDPOINT}/${refreshTokenId}`, {}, {
				headers: {
					'accept': 'application/json'
				}
			})

			const { accessToken, refreshToken: newRefreshToken } = response.data

			// Atualizar tokens no localStorage
			localStorage.setItem(AUTH_TOKEN, accessToken)
			localStorage.setItem(AUTH_REFRESH_TOKEN, newRefreshToken)

			// Processar fila de requisições pendentes
			processQueue(null, accessToken)
			isRefreshing = false

			// Retry da requisição original
			originalRequest.headers[TOKEN_PAYLOAD_KEY] = `Bearer ${accessToken}`
			return service(originalRequest)

		} catch (refreshError) {
			// Refresh falhou, fazer logout
			processQueue(refreshError, null)
			isRefreshing = false
			handleLogout()
			return Promise.reject(refreshError)
		}
	}

	// Outros erros
	handleOtherErrors(error)
	return Promise.reject(error)
})

// Função para extrair ID do refresh token
const extractRefreshTokenId = (refreshToken) => {
	// Ajuste conforme sua implementação
	// Se o refresh token já é o ID, retorna ele
	// Se for um JWT, você pode decodificar e extrair o ID
	return refreshToken
}

// Função para fazer logout
const handleLogout = () => {
	notification.error({
		message: 'Sessão Expirada',
		description: 'Por favor, faça login novamente'
	})
	localStorage.removeItem(AUTH_TOKEN)
	localStorage.removeItem(AUTH_REFRESH_TOKEN)
	store.dispatch(signOutSuccess())
}

// Função para tratar outros erros
const handleOtherErrors = (error) => {
	let notificationParam = {
		message: ''
	}

	if (error.response?.status === 404) {
		notificationParam.message = 'Recurso não encontrado'
	} else if (error.response?.status === 500) {
		notificationParam.message = 'Erro interno do servidor'
	} else if (error.response?.status === 508) {
		notificationParam.message = 'Timeout da requisição'
	} else if (error.code === 'NETWORK_ERROR' || !error.response) {
		notificationParam.message = 'Erro de conexão'
		notificationParam.description = 'Verifique sua conexão com a internet'
	}

	if (notificationParam.message) {
		notification.error(notificationParam)
	}
}

export default service